<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="1100px"
    scrollable
    persistent
  >
    <v-card class="rounded-xl border-light bg-white">
      <!-- Toolbar -->
      <v-toolbar flat color="white" class="border-bottom" height="60">
        <v-icon color="primary" class="mr-2">mdi-clipboard-text-outline</v-icon>
        <v-toolbar-title class="font-weight-bold grey--text text--darken-4 text-subtitle-1">
          Pegar Líneas Comerciales desde Excel
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4">
        <!-- Instrucciones y Área de Pegado -->
        <div class="mb-3">
          <div class="caption grey--text text--darken-2 mb-1">
            Copie y pegue las columnas desde Excel en el siguiente orden aproximado:
            <strong class="black--text">Cliente | Ejecutivo | Producto | Cód SAP | Marca | Empaque | Tipo (Bulk/Retail) | Incoterm | Destino | Cond. Pago | Capacidad TM/FCL | Base (TM/FCL) | TM | FCL | Precio Pactado | Precio FOB Eq | Obs</strong>
          </div>
          <s-textarea
            v-model="rawClipboardText"
            :rows="4"
            placeholder="Haga clic aquí y presione Ctrl + V para pegar los datos copiados del Excel..."
            @input="parseClipboard"
          />
        </div>

        <!-- Vista Previa de Filas Parseadas -->
        <div v-if="parsedRows.length > 0">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-2 font-weight-bold grey--text text--darken-3">
              Vista Previa de Filas ({{ parsedRows.length }})
            </span>
            <v-chip v-if="hasErrors" small color="red lighten-5" text-color="red darken-4" class="font-weight-bold">
              Hay filas con datos incompletos o erróneos
            </v-chip>
            <v-chip v-else small color="green lighten-5" text-color="green darken-4" class="font-weight-bold">
              Todas las filas son válidas
            </v-chip>
          </div>

          <v-data-table
            :headers="previewHeaders"
            :items="parsedRows"
            dense
            :items-per-page="10"
            class="border-rounded border-light elevation-0"
          >
            <template v-slot:item.index="{ index }">
              <span class="font-weight-bold text--secondary">{{ index + 1 }}</span>
            </template>

            <template v-slot:item.CustomerName="{ item }">
              <span>{{ item.CustomerName || 'Sin Cliente' }}</span>
            </template>

            <template v-slot:item.Product="{ item }">
              <span class="font-weight-bold" :class="!item.Product ? 'red--text' : ''">
                {{ item.Product || 'REQUERIDO' }}
              </span>
            </template>

            <template v-slot:item.CommercialType="{ item }">
              <span>{{ item.CommercialType === 2 ? 'Retail' : 'Bulk' }}</span>
            </template>

            <template v-slot:item.Incoterm="{ item }">
              <span class="font-weight-bold">{{ getIncotermText(item.Incoterm) }}</span>
            </template>

            <template v-slot:item.Capacity="{ item }">
              <span class="text-right font-weight-medium" :class="item.Capacity <= 0 ? 'red--text font-weight-bold' : ''">
                {{ item.Capacity }}
              </span>
            </template>

            <template v-slot:item.ProgrammedTons="{ item }">
              <span>{{ formatNumber(item.ProgrammedTons, 2) }}</span>
            </template>

            <template v-slot:item.ProgrammedFcl="{ item }">
              <span>{{ formatNumber(item.ProgrammedFcl, 2) }}</span>
            </template>

            <template v-slot:item.PriceAgreed="{ item }">
              <span>${{ formatNumber(item.PriceAgreed, 4) }}</span>
            </template>

            <template v-slot:item.PriceFobEquivalent="{ item }">
              <span>${{ formatNumber(item.PriceFobEquivalent, 4) }}</span>
            </template>

            <template v-slot:item.Status="{ item }">
              <v-chip v-if="item._error" x-small color="red lighten-4" text-color="red darken-4">
                {{ item._error }}
              </v-chip>
              <v-chip v-else x-small color="green lighten-4" text-color="green darken-4">
                Válido
              </v-chip>
            </template>
          </v-data-table>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4 border-top">
        <v-btn text small class="text-capitalize" @click="clearData">Limpiar</v-btn>
        <v-spacer />
        <v-btn text small class="text-capitalize" @click="closeModal">Cancelar</v-btn>
        <v-btn
          color="primary"
          small
          class="text-capitalize rounded-lg px-4"
          :disabled="parsedRows.length === 0 || hasErrors"
          @click="confirmImport"
        >
          <v-icon left small>mdi-check</v-icon>
          Importar {{ parsedRows.length }} Líneas
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "CommercialProgrammingPasteExcelModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rawClipboardText: "",
      parsedRows: [],
      previewHeaders: [
        { text: "#", value: "index", align: "center", sortable: false, width: "45px" },
        { text: "Cliente", value: "CustomerName", sortable: false },
        { text: "Ejecutivo", value: "Executive", sortable: false },
        { text: "Producto *", value: "Product", sortable: false },
        { text: "Marca", value: "Brand", sortable: false },
        { text: "Empaque", value: "Packaging", sortable: false },
        { text: "Tipo", value: "CommercialType", sortable: false },
        { text: "Incoterm", value: "Incoterm", sortable: false },
        { text: "Destino", value: "PortDestination", sortable: false },
        { text: "Capacidad", value: "Capacity", align: "right", sortable: false },
        { text: "TM", value: "ProgrammedTons", align: "right", sortable: false },
        { text: "FCL", value: "ProgrammedFcl", align: "right", sortable: false },
        { text: "Precio Pactado", value: "PriceAgreed", align: "right", sortable: false },
        { text: "Precio FOB Eq", value: "PriceFobEquivalent", align: "right", sortable: false },
        { text: "Estado", value: "Status", align: "center", sortable: false },
      ],
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    hasErrors() {
      return this.parsedRows.some((r) => !!r._error);
    },
  },
  methods: {
    parseClipboard() {
      if (!this.rawClipboardText || !this.rawClipboardText.trim()) {
        this.parsedRows = [];
        return;
      }

      const lines = this.rawClipboardText.trim().split("\n");
      const result = [];

      lines.forEach((lineStr, lineIdx) => {
        if (!lineStr.trim()) return;

        const cells = lineStr.split("\t").map((c) => c.trim());
        if (cells.length === 0 || (cells.length === 1 && !cells[0])) return;

        // Mapeo flexible de celdas
        const customer = cells[0] || "";
        const executive = cells[1] || "";
        const product = cells[2] || "";
        const sapCode = cells[3] || "";
        const brand = cells[4] || "";
        const packaging = cells[5] || "";
        const commTypeStr = (cells[6] || "").toLowerCase();
        const incotermStr = (cells[7] || "").toUpperCase();
        const port = cells[8] || "";
        const payment = cells[9] || "";
        const capacityVal = parseFloat(cells[10]) || 20;
        const entryBaseStr = (cells[11] || "").toLowerCase();
        const tonsVal = parseFloat(cells[12]) || 0;
        const fclVal = parseFloat(cells[13]) || 0;
        const priceAgreedVal = parseFloat(cells[14]) || 0;
        const priceFobEqVal = parseFloat(cells[15]) || priceAgreedVal;
        const observation = cells[16] || "";

        let incotermId = 1; // Default FOB
        if (incotermStr.includes("CFR")) incotermId = 2;
        else if (incotermStr.includes("CIF")) incotermId = 3;
        else if (incotermStr.includes("EXW")) incotermId = 4;
        else if (incotermStr.includes("FCA")) incotermId = 5;
        else if (incotermStr.includes("CIP")) incotermId = 6;
        else if (incotermStr.includes("CPT")) incotermId = 7;
        else if (incotermStr.includes("DAP")) incotermId = 8;
        else if (incotermStr.includes("DDP")) incotermId = 9;

        const commType = commTypeStr.includes("retail") ? 2 : 1;
        const entryBase = entryBaseStr.includes("fcl") ? 2 : 1;

        let error = null;
        if (!product) {
          error = "Falta Producto";
        } else if (capacityVal <= 0) {
          error = "Capacidad debe ser > 0";
        }

        result.push({
          CustomerName: customer,
          CustomerID: null,
          Executive: executive,
          Product: product,
          SapCode: sapCode,
          Brand: brand,
          Packaging: packaging,
          CommercialType: commType,
          Incoterm: incotermId,
          PortDestination: port,
          PaymentCondition: payment,
          Capacity: capacityVal,
          EntryBase: entryBase,
          ProgrammedTons: tonsVal,
          ProgrammedFcl: fclVal || (capacityVal > 0 ? tonsVal / capacityVal : 0),
          PriceAgreed: priceAgreedVal,
          PriceFobEquivalent: incotermId === 1 ? priceAgreedVal : priceFobEqVal,
          Observation: observation,
          _error: error,
        });
      });

      this.parsedRows = result;
    },

    getIncotermText(id) {
      switch (id) {
        case 1: return "FOB";
        case 2: return "CFR";
        case 3: return "CIF";
        case 4: return "EXW";
        case 5: return "FCA";
        case 6: return "CIP";
        case 7: return "CPT";
        case 8: return "DAP";
        case 9: return "DDP";
        default: return "FOB";
      }
    },

    confirmImport() {
      if (this.hasErrors || this.parsedRows.length === 0) return;
      this.$emit("import", this.parsedRows);
      this.closeModal();
    },

    clearData() {
      this.rawClipboardText = "";
      this.parsedRows = [];
    },

    closeModal() {
      this.dialogVisible = false;
    },

    formatNumber(val, decimals = 2) {
      const num = parseFloat(val);
      if (isNaN(num)) return "0.00";
      return num.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e2e8f0;
}

.border-top {
  border-top: 1px solid #e2e8f0;
}

.border-light {
  border: 1px solid #e2e8f0;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
  max-height: 400px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.preview-table th {
  position: sticky;
  top: 0;
  background-color: #f1f5f9;
  color: #334155;
  font-weight: 700;
  font-size: 0.74rem;
  text-transform: uppercase;
  padding: 8px 6px;
  border-bottom: 2px solid #cbd5e1;
  white-space: nowrap;
}

.preview-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.row-error td {
  background-color: #fff1f2 !important;
}
</style>
